const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/pages/appointment/components/PlanAndTreatmentBlock.tsx';
import { useEffect, useMemo, useState } from 'react';
import { SelectChangeEvent } from '@mui/material';
import Grid from '@mui/material/Grid';
import PlanSelector from 'App/pages/appointment/components/PlanSelector';
import { TreatmentReasonSelector } from './TreatmentReasonSelector';
import { useTheme } from '@mui/material/styles';
import { css } from '@emotion/css';
import { Plan, TreatmentReasonListItem } from 'App/defs/types';
import { getSlotDuration } from 'App/utils/appointment';
import { TreatmentReason } from '@cistec/common_web/defs/kisim';
import { useSchedulingPlans } from 'App/store/schedulingPlan';

export type PlandAndTreatmentBlockProps = {
    onChange: (plan: Plan | undefined, treatmentReason: TreatmentReasonListItem | undefined) => void;
    defaultValues?: {
        plan: Plan | null;
        treatmentReason: TreatmentReasonListItem | null;
    };
};

function useCSSClasses() {
    const theme = useTheme();
    return useMemo(
        () => ({
            root: css`
                padding: 0;
                border: none;
                white-space: normal;
                justify-content: space-between;

                & > div > div.MuiSelect-select {
                    display: flex;
                    align-items: center;
                    line-height: 1.2em;
                }

                & > label {
                    left: unset;
                }

                & > disabled {
                    color: #777777;
                }
                & > div > div > div.lineClampText {
                    overflow: hidden;
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                    word-break: break-word;
                }
            `,
        }),
        [theme]
    );
}

export const PlandAndTreatmentBlock = (props: PlandAndTreatmentBlockProps) => {
    const selectClasses = useCSSClasses();

    const [selectedPlan, setSelectedPlan] = useState<Plan | undefined>();
    const [selectedTreatmentReason, setSelectedTreatmentReason] = useState<TreatmentReasonListItem | undefined>();

    const { data: plans, isPendingOrLoading: plansAreLoading } = useSchedulingPlans();

    const createTreatmentReasonListItem = (reason: TreatmentReason): TreatmentReasonListItem => {
        const slotDuration = getSlotDuration(reason);
        const treatmentReason: TreatmentReasonListItem = {
            name: reason.Name,
            fkey: reason.Key,
        };
        if (slotDuration) {
            treatmentReason['duration'] = slotDuration;
        }
        return treatmentReason;
    };

    const createPlanListItem = (plan: any): Plan => ({
        name: plan.Name,
        label: plan.Name,
        Icode: plan.PlanIcode,
        value: plan.PlanIcode,
        treatmentReasons: plan.treatmentReasons.map(createTreatmentReasonListItem),
    });

    const planList = plans && plans.filter((plan: any) => plan.treatmentReasons).map(createPlanListItem);

    useEffect(() => {
        setSelectedTreatmentReason(undefined);
    }, [selectedPlan]);

    useEffect(() => {
        props.onChange(selectedPlan, selectedTreatmentReason);
    }, [selectedPlan, selectedTreatmentReason]);

    const handlePlanChange = (event: SelectChangeEvent<string>) => {
        const plan = planList!.find(plan => plan.Icode === event.target.value);
        setSelectedPlan(plan);
    };

    const handleTreatmentReasonChange = (event: SelectChangeEvent<string>) => {
        const treatmentReason = selectedPlan?.treatmentReasons!.find(
            (treatmentReason: TreatmentReasonListItem) => treatmentReason.fkey === event.target.value
        );
        setSelectedTreatmentReason(treatmentReason);
    };

    return (
        <Grid container spacing={2}>
            <PlanSelector
                classes={selectClasses}
                plans={plans}
                plansAreLoading={plansAreLoading}
                onChange={handlePlanChange}
                planList={planList || []}
                defaultValue={props.defaultValues?.plan || null}
            />
            <TreatmentReasonSelector
                reasonList={selectedPlan?.treatmentReasons!}
                classes={selectClasses}
                onChange={handleTreatmentReasonChange}
                defaultValue={props.defaultValues?.treatmentReason || null}
                value={selectedTreatmentReason || null}
            />
        </Grid>
    );
};
